import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TransactionTable = ({ data, loading = false }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig?.key === key && sortConfig?.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  const sortedData = React.useMemo(() => {
    const sorted = [...data]?.sort((a, b) => {
      if (sortConfig?.key === 'amount') {
        return sortConfig?.direction === 'asc' 
          ? parseFloat(a?.amount) - parseFloat(b?.amount)
          : parseFloat(b?.amount) - parseFloat(a?.amount);
      }
      if (sortConfig?.key === 'createdAt') {
        return sortConfig?.direction === 'asc'
          ? new Date(a?.createdAt) - new Date(b?.createdAt)
          : new Date(b?.createdAt) - new Date(a?.createdAt);
      }
      return 0;
    });
    return sorted;
  }, [data, sortConfig]);

  const paginatedData = sortedData?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(sortedData?.length / itemsPerPage);

  const getStatusBadge = (status) => {
    const configs = {
      success: { color: 'bg-success/10 text-success border-success/20', label: 'Success', icon: 'CheckCircle' },
      pending: { color: 'bg-warning/10 text-warning border-warning/20', label: 'Pending', icon: 'Clock' },
      failed: { color: 'bg-error/10 text-error border-error/20', label: 'Failed', icon: 'XCircle' }
    };
    const config = configs?.[status] || configs?.pending;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${config?.color}`}>
        <Icon name={config?.icon} size={12} />
        {config?.label}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return `Rp ${(parseFloat(amount) || 0)?.toLocaleString('id-ID')}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date?.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl p-4 md:p-6 shadow-sm animate-pulse">
        <div className="h-6 bg-muted rounded w-1/3 mb-6"></div>
        <div className="space-y-3">
          {[...Array(10)]?.map((_, i) => (
            <div key={i} className="h-16 bg-muted rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (data?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-xl p-8 shadow-sm text-center">
        <Icon name="Receipt" size={48} className="mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">No Transactions Found</h3>
        <p className="text-muted-foreground">This merchant hasn't processed any transactions yet</p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Transaction ID</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Customer</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Payment Method</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Status</th>
              <th className="px-4 py-3 text-right">
                <button
                  onClick={() => handleSort('amount')}
                  className="flex items-center gap-2 ml-auto text-sm font-semibold text-foreground hover:text-primary transition-colors"
                >
                  Amount
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('createdAt')}
                  className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors"
                >
                  Date
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {paginatedData?.map((transaction) => (
              <tr key={transaction?.id} className="hover:bg-muted/30 transition-colors">
                <td className="px-4 py-4">
                  <span className="text-sm font-medium text-foreground">{transaction?.transactionId}</span>
                </td>
                <td className="px-4 py-4">
                  <span className="text-sm text-foreground">{transaction?.customerName || '-'}</span>
                </td>
                <td className="px-4 py-4">
                  <span className="text-sm text-foreground capitalize">{transaction?.paymentMethod?.replace('_', ' ')}</span>
                </td>
                <td className="px-4 py-4">
                  {getStatusBadge(transaction?.status)}
                </td>
                <td className="px-4 py-4 text-right">
                  <span className="text-sm font-medium text-foreground">{formatCurrency(transaction?.amount)}</span>
                </td>
                <td className="px-4 py-4">
                  <span className="text-sm text-muted-foreground">{formatDate(transaction?.createdAt)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-border">
          <p className="text-sm text-muted-foreground">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, sortedData?.length)} of {sortedData?.length} transactions
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              <Icon name="ChevronLeft" size={16} />
            </Button>
            <span className="text-sm text-foreground">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              <Icon name="ChevronRight" size={16} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionTable;